// Shared KCC helpers — single source of truth for the small utilities that were
// previously copy-pasted across the Tenants / TenantDetail / Audit / Access
// service helpers, transformers, and components.

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// "Deployed 2 days ago" labels: ISO (UTC) timestamp → dayjs relative wording
// ("a day ago", "2 months ago", or "in 3 hours" for a future stamp). Empty
// string for null/undefined/unparseable input so callers can simply hide it.
export const timeAgo = (iso?: string | null): string => {
	if (!iso) return "";
	const stamp = dayjs(iso);
	return stamp.isValid() ? stamp.fromNow() : "";
};

// The backend wraps every response in ResponseModel ({ data, message, error }).
// apiService returns that envelope verbatim; `unwrap` pulls the inner `data`.
export interface IEnvelope<T> {
	data: T;
	message?: string;
	error?: unknown;
}

export const unwrap = <T,>(res: unknown): T => (res as IEnvelope<T>)?.data;

// Pull a human-readable message out of a FastAPI error ({ detail: {...} | str
// | ValidationError[] }), falling back to the supplied default. Used by every
// KCC mutation flow. The array shape is FastAPI's default 422 response — the
// server-side rules the FE doesn't mirror (e.g. expiry-not-past on PATCH)
// were previously swallowed into the generic fallback.
export const apiErrorMessage = (err: unknown, fallback: string): string => {
	const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
	if (Array.isArray(detail)) {
		const msgs = detail
			.map((item) => {
				const msg = (item as { msg?: unknown })?.msg;
				// Pydantic prefixes custom validator errors with "Value error, ".
				return typeof msg === "string" ? msg.replace(/^Value error, /, "") : null;
			})
			.filter(Boolean);
		if (msgs.length) return msgs.join(" · ");
	}
	if (detail && typeof detail === "object" && "message" in detail) {
		const msg = (detail as { message?: unknown }).message;
		if (typeof msg === "string") return msg;
	}
	if (typeof detail === "string") return detail;
	return fallback;
};

// Friendly display name from an email local-part: souvik.saha@zs.com → "Souvik Saha".
export const nameFromEmail = (email?: string): string => {
	if (!email) return "";
	const local = email.split("@")[0] ?? "";
	const name = local
		.split(/[._-]+/)
		.filter(Boolean)
		.map((p) => p.charAt(0).toUpperCase() + p.slice(1))
		.join(" ");
	return name || email;
};

// Canonical tenant instance URL from a tenant code, e.g. "pfiz" ->
// "https://pfiz.kural.zsservices.com/". Used as the default link when a tenant
// has no explicit tenant_url yet; the operator can override it in Overview.
export const buildTenantUrl = (tenantCode?: string): string =>
	tenantCode ? `https://${tenantCode.trim().toLowerCase()}.kural.zsservices.com/` : "";

// Two-letter initials for an avatar, from a display name.
export const initials = (name?: string): string => {
	if (!name) return "?";
	const parts = name.trim().split(/\s+/);
	const first = parts[0]?.[0] ?? "";
	const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
	return (first + last).toUpperCase() || "?";
};

// Currency amount for display, always to 2 decimal places. USD renders as
// "$1,234.50"; other currencies prefix the ISO code ("EUR 1,234.50").
export const money = (n: number, currency = "USD"): string =>
	`${currency === "USD" ? "$" : `${currency} `}${n.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

// Compact currency: drops ".00" for whole amounts (e.g. "$15"), but keeps up to
// two decimals for fractional values ("$15.50"). Used where round-dollar caps
// read cleaner than always-2dp — e.g. the AI plan/quota cards.
export const moneyCompact = (n: number, currency = "USD"): string =>
	`${currency === "USD" ? "$" : `${currency} `}${n.toLocaleString("en-US", {
		minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
		maximumFractionDigits: 2,
	})}`;

// ── Mirrors of the backend tenant validation (request_tenant.py) ─────────────
// The FE applies the same rules inline so users get instant feedback, but the
// backend remains the enforcement point. Allowed POC domains come from
// GET /tenants/validation-config (KCC_ALLOWED_POC_DOMAINS) — never hardcoded —
// so both sides read one source of truth.

// Same shape as the backend's _EMAIL_RE: dot-atom local part + hyphen-safe
// domain labels + a real TLD (stricter than antd's default email rule).
export const POC_EMAIL_RE =
	/^[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

// Same shape as the backend's _TENANT_CODE_RE: exactly 4 letters (product
// decision 2026-08-16). The code keys every /tenants/{code} path.
export const TENANT_CODE_RE = /^[A-Za-z]{4}$/;

// Domain allow-list check. An empty list means the check is disabled (matches
// the backend's empty-KCC_ALLOWED_POC_DOMAINS escape hatch) — and also covers
// the config fetch failing: the backend still enforces on submit.
export const isAllowedPocDomain = (email: string, allowedDomains: string[]): boolean => {
	if (allowedDomains.length === 0) return true;
	const domain = email.trim().toLowerCase().split("@").pop() ?? "";
	return allowedDomains.includes(domain);
};

// CSV cell encoder for the client-side exports (Tenants, Audit). Two jobs:
// 1. RFC-4180 quoting — wrap in quotes and double embedded quotes so commas,
//    quotes and newlines can't break the row structure.
// 2. Spreadsheet-formula neutralisation (VAPT 848804: CSV injection) — a cell
//    starting with =, +, -, @, tab or CR executes as a formula when the export
//    is opened in Excel/Sheets, so it is prefixed with a single quote (OWASP).
//    Excel hides the apostrophe and shows the original text.
export const csvCell = (value: string | null | undefined): string => {
	const raw = value ?? "";
	const neutralised = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
	return `"${neutralised.replace(/"/g, '""')}"`;
};
